import { inferMealType, type LoggedMeal, type MealEstimate, type MealType } from "./types";
import { supabase } from "./supabase";

const LOCAL_KEY = "cc.meals.v1";

// crypto.randomUUID is only available in secure contexts (HTTPS or localhost).
export function makeId(): string {
  const c = typeof crypto !== "undefined" ? crypto : undefined;
  if (c?.randomUUID) return c.randomUUID();
  if (c?.getRandomValues) {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// ---------------------------------------------------------------------------
// Pure helpers (work on any LoggedMeal[])
// ---------------------------------------------------------------------------
export function mealsByType(meals: LoggedMeal[]): Record<MealType, LoggedMeal[]> {
  const buckets: Record<MealType, LoggedMeal[]> = {
    breakfast: [],
    lunch: [],
    dinner: [],
    snack: []
  };
  for (const m of meals) buckets[m.meal_type].push(m);
  return buckets;
}

export function mealsOnDate(meals: LoggedMeal[], dateISO: string): LoggedMeal[] {
  // Critical: convert each meal's UTC timestamp to the user's LOCAL date
  // before comparing. Without this, evening meals get bucketed to "tomorrow"
  // (their UTC date) and disappear from today's dashboard. dateISO is already
  // local (comes from todayISO() or DateNav).
  return meals.filter((m) => localDateOf(m.logged_at) === dateISO);
}

export function todayISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Convert an ISO UTC timestamp (the format `logged_at` is stored in) into
 * YYYY-MM-DD in the user's local timezone. Use this any time you bucket
 * meals by day for display — without it, the day's last evening meals get
 * grouped into the NEXT day (their UTC date).
 */
export function localDateOf(isoTimestamp: string): string {
  const d = new Date(isoTimestamp);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// ---------------------------------------------------------------------------
// Supabase-backed CRUD
// ---------------------------------------------------------------------------
export interface SaveMealInput {
  meal_type: MealType;
  logged_at?: string;
  estimate: MealEstimate;
  caption?: string;
  source_providers: string[];
  disagreement_pct?: number;
  imageDataUrl?: string;
  externalPhotoUrl?: string;
}

export async function loadMealsRemote(): Promise<LoggedMeal[]> {
  if (!supabase) throw new Error("Supabase not configured");
  // CRITICAL: filter by user_id explicitly. The partner-sharing migration
  // added a "meals partner read" RLS policy that lets accepted partners
  // read each other's meals (for PartnerDay view). Without this filter,
  // `select * from meals` returns BOTH the user's meals AND their
  // partner's meals — which then get bucketed into the dashboard as if
  // they were the user's own. Real bug found in beta: wife saw James's
  // breakfast in her own Today section, and any meal he added later
  // auto-appeared in her day too. Filter prevents the leak; PartnerDay
  // has its own loader (loadPartnerMealsRemote) that queries an explicit
  // partner user_id when the recipient wants to view the sharer's day.
  const {
    data: { user }
  } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from("meals")
    .select("*")
    .eq("user_id", user.id)
    .order("logged_at", { ascending: false });
  if (error) throw error;

  const meals: LoggedMeal[] = [];
  for (const row of data ?? []) {
    let photo_data_url: string | undefined;
    if (row.photo_path) {
      const { data: signed } = await supabase.storage
        .from("meal-photos")
        .createSignedUrl(row.photo_path, 60 * 60);
      photo_data_url = signed?.signedUrl;
    } else if (row.external_photo_url) {
      photo_data_url = row.external_photo_url;
    }
    meals.push({
      id: row.id,
      user_id: row.user_id,
      logged_at: row.logged_at,
      meal_type: row.meal_type,
      photo_data_url,
      caption: row.caption ?? undefined,
      estimate: row.estimate,
      source_providers: row.source_providers ?? [],
      disagreement_pct: row.disagreement_pct ?? undefined
    });
  }
  return meals;
}

export async function saveMealRemote(input: SaveMealInput): Promise<LoggedMeal> {
  if (!supabase) throw new Error("Supabase not configured");
  const { data: userData } = await supabase.auth.getUser();
  const user = userData.user;
  if (!user) throw new Error("Not signed in");

  const id = makeId();
  let photo_path: string | null = null;

  if (input.imageDataUrl) {
    const blob = await dataUrlToBlob(input.imageDataUrl);
    photo_path = `${user.id}/${id}.jpg`;
    const { error: upErr } = await supabase.storage
      .from("meal-photos")
      .upload(photo_path, blob, { contentType: blob.type, upsert: true });
    if (upErr) throw upErr;
  }

  const external_photo_url = !photo_path && input.externalPhotoUrl ? input.externalPhotoUrl : null;

  const row = {
    id,
    user_id: user.id,
    logged_at: input.logged_at ?? new Date().toISOString(),
    meal_type: input.meal_type,
    photo_path,
    external_photo_url,
    caption: input.caption ?? null,
    estimate: input.estimate,
    source_providers: input.source_providers,
    disagreement_pct: input.disagreement_pct ?? null
  };

  const { error } = await supabase.from("meals").insert(row);
  if (error) throw error;

  let photo_data_url: string | undefined;
  if (photo_path) {
    const { data: signed } = await supabase.storage
      .from("meal-photos")
      .createSignedUrl(photo_path, 60 * 60);
    photo_data_url = signed?.signedUrl;
  } else if (external_photo_url) {
    photo_data_url = external_photo_url;
  }

  return {
    id,
    user_id: user.id,
    logged_at: row.logged_at,
    meal_type: input.meal_type,
    photo_data_url,
    caption: input.caption,
    estimate: input.estimate,
    source_providers: input.source_providers,
    disagreement_pct: input.disagreement_pct
  };
}

export async function loadMealRemote(id: string): Promise<LoggedMeal | null> {
  if (!supabase) throw new Error("Supabase not configured");
  const { data, error } = await supabase.from("meals").select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  if (!data) return null;
  let photo_data_url: string | undefined;
  if (data.photo_path) {
    const { data: signed } = await supabase.storage
      .from("meal-photos")
      .createSignedUrl(data.photo_path, 60 * 60);
    photo_data_url = signed?.signedUrl;
  } else if (data.external_photo_url) {
    photo_data_url = data.external_photo_url;
  }
  return {
    id: data.id,
    user_id: data.user_id,
    logged_at: data.logged_at,
    meal_type: data.meal_type,
    photo_data_url,
    caption: data.caption ?? undefined,
    estimate: data.estimate,
    source_providers: data.source_providers ?? [],
    disagreement_pct: data.disagreement_pct ?? undefined
  };
}

export interface UpdateMealInput {
  meal_type: MealType;
  estimate: MealEstimate;
  caption?: string;
}

export async function updateMealRemote(id: string, patch: UpdateMealInput): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const { error } = await supabase
    .from("meals")
    .update({
      meal_type: patch.meal_type,
      estimate: patch.estimate,
      caption: patch.caption ?? null
    })
    .eq("id", id);
  if (error) throw error;
}

export async function deleteMealRemote(id: string): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  // Fetch photo_path so we can delete from Storage too.
  const { data: row } = await supabase.from("meals").select("photo_path").eq("id", id).single();
  const { error } = await supabase.from("meals").delete().eq("id", id);
  if (error) throw error;
  if (row?.photo_path) {
    await supabase.storage.from("meal-photos").remove([row.photo_path]);
  }
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const res = await fetch(dataUrl);
  return res.blob();
}

// ---------------------------------------------------------------------------
// Legacy localStorage (used during migration prompt only)
// ---------------------------------------------------------------------------
export function loadLocalMeals(): LoggedMeal[] {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((m: Partial<LoggedMeal>) => {
      if (!m.meal_type) {
        const when = m.logged_at ? new Date(m.logged_at) : new Date();
        return { ...m, meal_type: inferMealType(when) } as LoggedMeal;
      }
      return m as LoggedMeal;
    });
  } catch {
    return [];
  }
}

export function clearLocalMeals(): void {
  localStorage.removeItem(LOCAL_KEY);
}
